import React, { useEffect, useState, useMemo, Fragment } from 'react';
import { Formatter } from '../format';
import Entities from './Entities';
import { getParsedFileSummary } from '../api/api';

const parseSummaryField = (data) => {
  if (!data) return { summary: '', validations: null };
  try {
    const obj = typeof data === 'string' ? JSON.parse(data) : data;
    if (typeof obj === 'object' && obj) {
      return {
        summary: obj.summary || obj.Summary || '',
        validations: obj.validations || obj.Validations || null,
      };
    }
  } catch (_) {
    /* not JSON */
  }
  return { summary: data, validations: null };
};

/**
 * Parses a summary string with a custom format like "*Key* Value".
 */
const parseAsteriskFormattedSummary = (text) => {
  if (typeof text !== 'string' || text.trim().startsWith('{')) {
    return {};
  }
  const result = {};
  const regex = /\*([^*]+)\*(.*?)(?=\*[^*]+\*|$)/gs;
  let match;
  while ((match = regex.exec(text)) !== null) {
    const key = match[1].trim();
    let value = match[2].trim();
    const lowerKey = key.toLowerCase();
    if (lowerKey === 'actions') {
      result[lowerKey] = value
        .split('\n')
        .map((line) => line.trim().replace(/^-/, '').trim())
        .filter(Boolean);
    } else {
      result[lowerKey] = value;
    }
  }
  return result;
};

const formatAsteriskWrappedText = (text) => {
  if (!text) return null;
  const segments = text.split(/(\*[^*]+\*)/g);
  return segments.map((segment, index) => {
    if (!segment) return null;
    if (segment.startsWith('*') && segment.endsWith('*') && segment.length > 2) {
      return (
        <strong key={`bold-${index}`} className="font-semibold">
          {segment.slice(1, -1)}
        </strong>
      );
    }
    return <Fragment key={`text-${index}`}>{segment}</Fragment>;
  });
};

const CallDetailsPanel = ({ callData }) => {
  const parsedAsteriskSummary = useMemo(
    () => parseAsteriskFormattedSummary(callData?.summary_summary),
    [callData?.summary_summary]
  );

  const { summary: parsedInitialSummary } = useMemo(
    () => parseSummaryField(callData?.summary_summary),
    [callData?.summary_summary]
  );

  const initialSummaryText =
    parsedAsteriskSummary.summary ||
    parsedInitialSummary ||
    callData?.ConversationAnalytics?.ContactSummary?.AutoGenerated?.OverallSummary?.Content ||
    '';

  const [summaryText, setSummaryText] = useState(initialSummaryText);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  const formattedSummaryText = useMemo(
    () => formatAsteriskWrappedText(summaryText),
    [summaryText]
  );

  const genAiSummary = useMemo(() => {
    if (!callData) return [];
    const hiddenLabels = [
      'validations',
      'transcription',
      'contact summary',
      'topic',
      'product',
      'resolved',
      'actions',
    ];
    let summary = callData?.ConversationAnalytics?.Summary
      ? Object.entries(callData.ConversationAnalytics.Summary).map(
          ([key, value]) => ({
            label: key,
            value: Array.isArray(value) ? value.join(', ') : value,
          })
        )
      : [];

    const contactSummary =
      callData?.ConversationAnalytics?.ContactSummary?.AutoGenerated?.OverallSummary?.Content;
    if (contactSummary) {
      summary = [{ label: 'Contact Summary', value: contactSummary }, ...summary];
    }

    return summary.filter(
      (e) => {
        const normalized = (e.label ?? '')
          .toString()
          .trim()
          .toLowerCase()
          .replace(/:$/, '');
        return !hiddenLabels.includes(normalized);
      }
    );
  }, [callData]);

  useEffect(() => {
    const s = callData.summary_summary;
    if (s) {
      const parsed = parseSummaryField(s);
      const asteriskParsed = parseAsteriskFormattedSummary(s);
      setSummaryText(
        asteriskParsed.summary ||
          parsed.summary ||
          callData?.ConversationAnalytics?.ContactSummary?.AutoGenerated?.OverallSummary?.Content ||
          ''
      );
      return;
    }

    const contactSummary =
      callData?.ConversationAnalytics?.ContactSummary?.AutoGenerated?.OverallSummary?.Content;
    if (contactSummary) {
      setSummaryText(contactSummary);
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        setSummaryLoading(true);
        const filename = callData.key || callData.filename;
        if (!filename) {
          console.warn('No filename available for parsed summary.');
          return;
        }
        const parsed = await getParsedFileSummary(filename);
        if (cancelled) return;
        const parsedField = parseSummaryField(
          parsed?.summary_summary || parsed?.summary
        );
        const asteriskParsed = parseAsteriskFormattedSummary(
          parsed?.summary_summary || parsed?.summary
        );
        const text =
          asteriskParsed.summary ||
          parsedField.summary ||
          parsed?.ConversationAnalytics?.ContactSummary?.AutoGenerated?.OverallSummary?.Content;
        if (text) {
          setSummaryText(text);
        } else {
          setSummaryError('Summary unavailable.');
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) setSummaryError('Failed to load summary.');
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [callData]);

  if (!callData) {
    return (
      <div className="text-sm text-gray-500 !text-left" style={{ textAlign: 'left' }}>
        Select a call to view details.
      </div>
    );
  }

  const metadata = [
    {
      label: 'Timestamp',
      value: Formatter.Timestamp(
        callData.timestamp || callData?.ConversationAnalytics?.ConversationTime
      ),
    },
    {
      label: 'Job ID',
      value:
        callData.jobName ||
        callData?.ConversationAnalytics?.SourceInformation?.[0]?.TranscribeJobInfo
          ?.TranscriptionJobName,
    },
    { label: 'GUID', value: callData.guid || callData?.ConversationAnalytics?.GUID },
    { label: 'Conversation ID', value: callData.conversationId || callData?.ConversationAnalytics?.Telephony?.Genesys?.conversationId },
    { label: 'DNIS', value: callData.dnis || callData?.ConversationAnalytics?.Telephony?.Genesys?.dnis },
    { label: 'Agent', value: callData.agent || callData?.ConversationAnalytics?.Agent },
    { label: 'Customer', value: callData.customer || callData?.ConversationAnalytics?.Cust },
    { label: 'Queue', value: callData.queue || callData?.ConversationAnalytics?.Queue },
    {
      label: 'Language',
      value: callData.lang || callData?.ConversationAnalytics?.LanguageCode,
    },
    {
      label: 'Duration',
      value: Formatter.Time(
        callData.duration || callData?.ConversationAnalytics?.Duration
      ),
    },
    {
      label: 'Assessed By',
      value: callData.assessedBy || callData?.ConversationAnalytics?.AssessDetails?.assessedBy || '-',
    },
    {
      label: 'Assessed At',
      value: callData.assessedAt
        ? Formatter.Timestamp(callData.assessedAt)
        : (callData?.ConversationAnalytics?.AssessDetails?.assessedAt
            ? Formatter.Timestamp(callData.ConversationAnalytics.AssessDetails.assessedAt)
            : '-'),
    },
  ];

  const topic =
    parsedAsteriskSummary.topic ||
    callData.summary_topic ||
    callData?.ConversationAnalytics?.ContactSummary?.Topic ||
    callData?.ConversationAnalytics?.Summary?.Topic ||
    '-';

  const product =
    parsedAsteriskSummary.product ||
    callData.summary_product ||
    callData?.ConversationAnalytics?.ContactSummary?.Product ||
    callData?.ConversationAnalytics?.Summary?.Product ||
    '-';

  const resolved =
    parsedAsteriskSummary.resolved ||
    callData.summary_resolved ||
    callData?.ConversationAnalytics?.ContactSummary?.Resolved ||
    callData?.ConversationAnalytics?.Summary?.Resolved ||
    '-';

  const actions =
    parsedAsteriskSummary.actions ||
    (Array.isArray(callData.summary_actions)
      ? callData.summary_actions
      : Array.isArray(callData?.ConversationAnalytics?.Summary?.Actions)
      ? callData.ConversationAnalytics.Summary.Actions
      : Array.isArray(callData?.ConversationAnalytics?.ActionItemsDetected)
      ? callData.ConversationAnalytics.ActionItemsDetected.map((a) => a.Text)
      : []);

  const entities = Array.isArray(callData.entities)
    ? callData.entities
    : callData.analytics?.entities || [];

  const transcript = callData.transcript || '';

  return (
    // Global hard left alignment (inline style wins against rogue CSS)
    <div
      className="space-y-6 !text-left"
      style={{ textAlign: 'left' }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Metadata */}
        <div className="card !text-left" style={{ textAlign: 'left' }}>
          <h2 className="text-lg font-semibold mb-4">Metadata</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 !text-left">
            {metadata.map((m) => (
              <div key={m.label} className="space-y-1 !text-left">
                <dt className="text-sm font-medium text-gray-500">{m.label}</dt>
                <dd className="text-sm text-gray-900 break-words">{m.value || '-'}</dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Summary */}
        <div className="card !text-left" style={{ textAlign: 'left' }}>
          <h2 className="text-lg font-semibold mb-4">Summary</h2>

          {/* Basic Info Section */}
          <div className="space-y-4 mb-4 !text-left">
            <div className="grid grid-cols-1 gap-y-3 !text-left">
              <div>
                <span className="text-sm font-medium text-gray-500">Topic: </span>
                <span className="text-sm text-gray-900">{topic}</span>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Product: </span>
                <span className="text-sm text-gray-900">{product}</span>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Resolved: </span>
                <span className="text-sm text-gray-900">{resolved}</span>
              </div>
            </div>
          </div>

          {/* Summary Text Section */}
          {summaryLoading ? (
            <p className="text-sm text-gray-500 !text-left">Loading summary...</p>
          ) : summaryText ? (
            <div className="mb-4 !text-left">
              <p className="text-sm text-gray-700 whitespace-pre-line !text-left">{formattedSummaryText}</p>
            </div>
          ) : summaryError ? (
            <p className="text-sm text-red-600 mb-4 !text-left">({summaryError})</p>
          ) : (
            <p className="text-sm text-gray-500 mb-4 !text-left">No summary available.</p>
          )}

          {/* Actions Section */}
          {actions.length > 0 && (
            <div className="mb-4 !text-left">
              <p className="text-sm font-medium text-gray-500 mb-2">Actions:</p>
              <ul className="space-y-1 list-disc pl-5">
                {actions.map((action, i) => (
                  <li key={i} className="text-sm text-gray-700 break-words">
                    {action}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Additional AI Summary Section */}
          {genAiSummary.length > 0 && (
            <div className="border-t pt-4 !text-left">
              <p className="text-sm font-medium text-gray-500 mb-2">Additional Details:</p>
              <dl className="space-y-2">
                {genAiSummary.map((entry, i) => (
                  <div key={i}>
                    <dt className="text-sm font-medium text-gray-700 inline">{entry.label}: </dt>
                    <dd className="text-sm text-gray-600 inline">{entry.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Entities */}
        {/* <div className="card !text-left" style={{ textAlign: 'left' }}>
          <h2 className="text-lg font-semibold mb-4">Entities</h2>
          <Entities entities={entities} />
        </div> */}

        {/* Transcription */}
        {/* <div className="card !text-left" style={{ textAlign: 'left' }}>
          <h2 className="text-lg font-semibold mb-4">Transcription</h2>
          {transcript ? (
            <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono">
              {transcript}
            </pre>
          ) : (
            <p className="text-sm text-gray-500">Transcript not available.</p>
          )}
        </div> */}
      </div>
    </div>
  );
};

export default CallDetailsPanel;